/**
 * MachineEditPage — Full-page 4-step machine wizard.
 * Routes:  /app/machines/new
 *          /app/machines/:machineId/edit
 *
 * Layout:
 *   56px navy header  (breadcrumb + back)
 *   body (flex row)
 *     240px left sidebar  (vertical Stepper)
 *     flex-1 content area (scrollable)
 *   64px footer  (Cancel / Back / Next)
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Stepper, Button, Group, TextInput, Stack, Text, Alert, Loader,
  Table, ActionIcon, Select, Badge, Tooltip, Divider, Checkbox,
  ScrollArea, Switch, Textarea, NumberInput, Title, Anchor,
  Breadcrumbs, Image,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { MdAdd, MdDelete, MdEdit, MdSave, MdClose, MdArrowBack } from 'react-icons/md';
import {
  getMachine, createMachine, updateMachine,
  getMachineChannels, createChannel, updateChannel, deleteChannel,
  getCalculatedChannels, createCalculatedChannel,
  updateCalculatedChannel, deleteCalculatedChannel,
  getAlarmRules, createAlarmRule, updateAlarmRule, deleteAlarmRule,
  getOrgs,
} from '../../API/adminAPI';
import ChannelBoard from './ChannelBoard';
import AtomsLogo from '../../assets/AtomsLogo.png';

// ── Constants ─────────────────────────────────────────────────────────────────

const ATOMS_BLUE = 'var(--accent)';

const TIMEZONE_OPTIONS = [
  { value: '330',  label: 'IST — India (UTC+5:30)' },
  { value: '240',  label: 'UAE / Dubai (UTC+4:00)' },
  { value: '0',    label: 'UTC (UTC+0:00)' },
  { value: '60',   label: 'CET — Central Europe (UTC+1)' },
  { value: '120',  label: 'EET — Eastern Europe (UTC+2)' },
  { value: '-300', label: 'EST — US Eastern (UTC-5)' },
  { value: '-360', label: 'CST — US Central (UTC-6)' },
];

const CHANNEL_TYPES = [
  { value: 'digital_input',  label: 'Digital Input' },
  { value: 'digital_output', label: 'Digital Output' },
  { value: 'analog_input',   label: 'Analog Input' },
  { value: 'analog_output',  label: 'Analog Output' },
  { value: 'other',          label: 'Other' },
];

const FORMULA_TYPES = [
  { value: 'elapsed_minus_channel',  label: 'Elapsed today − Channel  (e.g. Idle = today − Run Time)' },
  { value: 'channel_minus_channel',  label: 'Channel A − Channel B' },
  { value: 'channel_plus_channel',   label: 'Channel A + Channel B' },
  { value: 'channel_times_constant', label: 'Channel A × Constant' },
  { value: 'channel_div_constant',   label: 'Channel A ÷ Constant' },
  { value: 'custom',                 label: 'Custom formula (advanced)' },
];

const typeColor = {
  digital_input: 'blue', digital_output: 'cyan',
  analog_input: 'orange', analog_output: 'yellow', other: 'gray',
};

const CHANNEL_GROUP_META = [
  { key: 'digital_inputs', label: 'Digital Inputs' },
  { key: 'digital_outputs', label: 'Digital Outputs' },
  { key: 'analog_inputs', label: 'Analog Inputs' },
  { key: 'analog_outputs', label: 'Analog Outputs' },
  { key: 'other', label: 'Other' },
];

const needsChannelA = new Set(['elapsed_minus_channel','channel_minus_channel','channel_plus_channel','channel_times_constant','channel_div_constant']);
const needsChannelB = new Set(['channel_minus_channel','channel_plus_channel']);
const needsConstant = new Set(['channel_times_constant','channel_div_constant']);
const needsCustom   = new Set(['custom']);

const STEPS = [
  { label: 'Identity',   description: 'Machine details' },
  { label: 'Channels',   description: 'I/O channel registry' },
  { label: 'Display',    description: 'Channel visibility' },
  { label: 'Calculated', description: 'Formula channels' },
  { label: 'Alarms',     description: 'Alarm rules' },
];

const CONDITION_OPTIONS = [
  { value: 'gt',     label: 'Greater than (>)' },
  { value: 'gte',    label: 'Greater than or equal (≥)' },
  { value: 'lt',     label: 'Less than (<)' },
  { value: 'lte',    label: 'Less than or equal (≤)' },
  { value: 'eq',     label: 'Equals (=)' },
  { value: 'neq',    label: 'Not equals (≠)' },
  { value: 'is_on',  label: 'Is ON (digital)' },
  { value: 'is_off', label: 'Is OFF (digital)' },
];

const SEVERITY_OPTIONS = [
  { value: 'info',     label: 'ℹ️ Info' },
  { value: 'warning',  label: '⚠️ Warning' },
  { value: 'critical', label: '🚨 Critical' },
];

const DIGITAL_CONDITIONS = new Set(['is_on', 'is_off']);

const severityColor = { info: 'blue', warning: 'yellow', critical: 'red' };

// ── Page ─────────────────────────────────────────────────────────────────────

const MachineEditPage = () => {
  const { machineId } = useParams();
  const navigate      = useNavigate();
  const location      = useLocation();
  const role          = localStorage.getItem('role') || 'user';
  const isNew         = !machineId;

  const backPath = role === 'atoms_admin' ? '/app/super-admin' : '/app/admin-panel';

  // ── Initial data loading ─────────────────────────────────────────────────
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError,   setPageError]   = useState('');
  const [loadedMachine, setLoadedMachine] = useState(location.state?.machine || null);
  const [oems, setOems] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        if (isNew && role !== 'atoms_admin') {
          setPageError('Only ATOMS admins can commission machines');
          setPageLoading(false);
          return;
        }
        const tasks = [];
        if (machineId) {
          tasks.push(getMachine(machineId).then(setLoadedMachine));
        }
        if (role === 'atoms_admin') {
          tasks.push(
            getOrgs().then((d) =>
              setOems(
                (d.organisations || [])
                  .filter((o) => o.org_type === 'oem')
                  .map((o) => ({ value: String(o.id), label: o.name }))
              )
            )
          );
        }
        await Promise.all(tasks);
      } catch (e) {
        setPageError(e?.response?.data?.error || 'Failed to load machine data');
      } finally {
        setPageLoading(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (pageLoading) return (
    <PageShell backPath={backPath} title={isNew ? 'Commission New Machine' : 'Loading…'} crumb={isNew ? 'New' : '…'}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <Loader size="lg" />
      </div>
    </PageShell>
  );

  if (pageError) return (
    <PageShell backPath={backPath} title="Error" crumb="Error">
      <Alert color="red" m="xl">{pageError}</Alert>
    </PageShell>
  );

  return (
    <WizardContent
      machine={loadedMachine}
      oems={oems}
      backPath={backPath}
      onDone={(saved) => {
        navigate(backPath, { state: { refresh: saved, tab: 'machines' } });
      }}
    />
  );
};

// ── WizardContent — all 4 steps ───────────────────────────────────────────────

const WizardContent = ({ machine, oems, backPath, onDone }) => {
  const isEdit = Boolean(machine);
  const configuredSummary = machine?.configured_channel_summary || {};
  const observedSchema = machine?.observed_payload_schema || null;

  const [active,   setActive]   = useState(0);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [savedMachineId, setSavedMachineId] = useState(machine?.id || null);

  // Step 2
  const [channels,        setChannels]        = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [newChannels,     setNewChannels]     = useState([]);
  const [deleteQueue,     setDeleteQueue]     = useState(new Set());

  // Step 3
  const [dispCfg, setDispCfg] = useState({
    status_channels:       machine?.status_channels       || [],
    report_channels:       machine?.report_channels       || [],
    dashboard_channels:    machine?.dashboard_channels    || [],
    kpi_channels:          machine?.kpi_channels          || [],
    report_channel_labels: machine?.report_channel_labels || {},
  });
  // (report label editing now handled inline inside ChannelBoard)

  // Step 4
  const [calcChannels,    setCalcChannels]    = useState([]);
  const [calcLoading,     setCalcLoading]     = useState(false);
  const [deleteCalcQueue, setDeleteCalcQueue] = useState(new Set());
  const [newCalcRows,     setNewCalcRows]     = useState([]);
  const [editCalcId,      setEditCalcId]      = useState(null);
  const [editCalcData,    setEditCalcData]    = useState({});

  // Step 5 — Alarm Rules
  const [alarmRules,      setAlarmRules]      = useState([]);
  const [alarmLoading,    setAlarmLoading]    = useState(false);
  const [newAlarmRows,    setNewAlarmRows]    = useState([]);
  const [deleteAlarmQueue, setDeleteAlarmQueue] = useState(new Set());
  const BLANK_ALARM = () => ({ _key: Date.now(), channel_internal_name: '', condition: 'gt', threshold: '', severity: 'warning', title: '', message_template: '{machine} — {channel} triggered alarm (value: {value})', cooldown_minutes: 5, is_active: true });

  // Step 1 form
  const form = useForm({
    initialValues: {
      machine_id:              machine?.machine_id || '',
      machine_name:            machine?.machine_name || '',
      timezone_offset_minutes: String(machine?.timezone_offset_minutes ?? 330),
      owned_by_id:             machine?.owned_by_id ? String(machine.owned_by_id) : null,
    },
    validate: {
      machine_id:   (v) => /^[a-zA-Z0-9_-]+$/.test(v.trim()) ? null : 'Only letters, numbers, _ and - allowed',
      machine_name: (v) => v.trim() ? null : 'Machine name required',
    },
  });

  // Helpers
  const loadChannels = async (pk) => {
    setChannelsLoading(true);
    try { const d = await getMachineChannels(pk); setChannels(d.channels || []); }
    catch { setChannels([]); }
    finally { setChannelsLoading(false); }
  };

  const loadCalcChannels = async (pk) => {
    setCalcLoading(true);
    try { const d = await getCalculatedChannels(pk); setCalcChannels(d.calculated_channels || []); }
    catch { setCalcChannels([]); }
    finally { setCalcLoading(false); }
  };

  const loadAlarmRules = async (pk) => {
    setAlarmLoading(true);
    try { const d = await getAlarmRules(pk); setAlarmRules(d.alarm_rules || []); }
    catch { setAlarmRules([]); }
    finally { setAlarmLoading(false); }
  };

  const allChannelNames = [
    ...channels.filter((c) => !deleteQueue.has(c.id)).map((c) => c.internal_name),
    ...newChannels.map((nc) => nc.internal_name).filter(Boolean),
  ];

  const channelOptions = channels
    .filter((c) => !deleteQueue.has(c.id))
    .map((c) => ({ value: String(c.id), label: c.internal_name }));

  // ── Navigation ─────────────────────────────────────────────────────────────
  const nextStep = async () => {
    setError('');

    if (active === 0) {
      const v = form.validate();
      if (v.hasErrors) return;
      setSaving(true);
      try {
        let mId = savedMachineId;
        if (isEdit) {
          await updateMachine(savedMachineId, {
            machine_name:            form.values.machine_name.trim(),
            timezone_offset_minutes: parseInt(form.values.timezone_offset_minutes),
            ...(oems != null && { owned_by_id: form.values.owned_by_id ? parseInt(form.values.owned_by_id) : null }),
          });
        } else {
          const resp = await createMachine({
            machine_id:              form.values.machine_id.trim(),
            machine_name:            form.values.machine_name.trim(),
            timezone_offset_minutes: parseInt(form.values.timezone_offset_minutes),
            ...(oems != null && { owned_by_id: form.values.owned_by_id ? parseInt(form.values.owned_by_id) : null }),
          });
          mId = resp.machine?.id;
          setSavedMachineId(mId);
        }
        await loadChannels(mId);
        setActive(1);
      } catch (e) { setError(e?.response?.data?.error || 'Save failed'); }
      finally { setSaving(false); }
      return;
    }

    if (active === 1) {
      setSaving(true);
      try {
        await Promise.all([...deleteQueue].map((pk) => deleteChannel(savedMachineId, pk)));
        await Promise.all(
          channels.filter((ch) => !deleteQueue.has(ch.id)).map((ch) =>
            updateChannel(savedMachineId, ch.id, { display_name: ch.display_name, position: ch.position, is_oem_internal: ch.is_oem_internal })
          )
        );
        await Promise.all(
          newChannels.filter((nc) => nc.internal_name.trim() && nc.display_name.trim()).map((nc) =>
            createChannel(savedMachineId, { channel_type: nc.channel_type, internal_name: nc.internal_name.trim(), display_name: nc.display_name.trim(), position: nc.position, is_oem_internal: nc.is_oem_internal || false })
          )
        );
        await loadChannels(savedMachineId);
        setNewChannels([]); setDeleteQueue(new Set());
        setActive(2);
      } catch (e) { setError(e?.response?.data?.error || 'Channel save failed'); }
      finally { setSaving(false); }
      return;
    }

    if (active === 2) {
      setSaving(true);
      try {
        await updateMachine(savedMachineId, {
          status_channels:       dispCfg.status_channels,
          report_channels:       dispCfg.report_channels,
          dashboard_channels:    dispCfg.dashboard_channels.slice(0, 3),
          kpi_channels:          dispCfg.kpi_channels,
          report_channel_labels: dispCfg.report_channel_labels,
        });
        await loadCalcChannels(savedMachineId);
        setActive(3);
      } catch (e) { setError(e?.response?.data?.error || 'Display config save failed'); }
      finally { setSaving(false); }
      return;
    }

    if (active === 3) {
      setSaving(true);
      try {
        await Promise.all([...deleteCalcQueue].map((pk) => deleteCalculatedChannel(savedMachineId, pk)));
        await Promise.all(
          newCalcRows.filter((nc) => nc.name.trim() && nc.formula_type).map((nc) =>
            createCalculatedChannel(savedMachineId, {
              name: nc.name.trim(), formula_type: nc.formula_type,
              channel_a_id: nc.channel_a_id || null, channel_b_id: nc.channel_b_id || null,
              constant: nc.constant != null ? nc.constant : null,
              custom_formula: nc.custom_formula || '',
              channel_type: nc.channel_type || 'other', overwrite: nc.overwrite || false, position: nc.position || 0,
            })
          )
        );
        await loadAlarmRules(savedMachineId);
        setActive(4);
      } catch (e) { setError(e?.response?.data?.error || 'Calc channel save failed'); }
      finally { setSaving(false); }
      return;
    }

    if (active === 4) {
      setSaving(true);
      try {
        // Delete queued
        await Promise.all([...deleteAlarmQueue].map((pk) => deleteAlarmRule(savedMachineId, pk)));
        // Create new rows (must have channel + condition + title)
        await Promise.all(
          newAlarmRows.filter((r) => r.channel_internal_name.trim() && r.condition && r.title.trim()).map((r) =>
            createAlarmRule(savedMachineId, {
              channel_internal_name: r.channel_internal_name.trim(),
              condition:             r.condition,
              threshold:             DIGITAL_CONDITIONS.has(r.condition) ? null : (r.threshold !== '' ? parseFloat(r.threshold) : null),
              severity:              r.severity || 'warning',
              title:                 r.title.trim(),
              message_template:      r.message_template || '{machine} — {channel} triggered alarm (value: {value})',
              cooldown_minutes:      parseInt(r.cooldown_minutes) || 5,
              is_active:             r.is_active !== false,
            })
          )
        );
        onDone(true);
      } catch (e) { setError(e?.response?.data?.error || 'Alarm rule save failed'); }
      finally { setSaving(false); }
    }
  };

  const prevStep = () => setActive((a) => Math.max(0, a - 1));

  // Step 2 helpers
  const addNewRow    = () => setNewChannels((p) => [...p, { _key: Date.now(), channel_type: 'digital_input', internal_name: '', display_name: '', position: p.length + channels.length, is_oem_internal: false }]);
  const updateNewRow = (key, f, v) => setNewChannels((p) => p.map((r) => r._key === key ? { ...r, [f]: v } : r));
  const removeNewRow = (key) => setNewChannels((p) => p.filter((r) => r._key !== key));
  const queueDelete  = (pk)  => setDeleteQueue((p) => new Set([...p, pk]));
  const updateExisting = (id, f, v) => setChannels((p) => p.map((c) => c.id === id ? { ...c, [f]: v } : c));

  // Step 3 helpers
  const setDisp = (f, v) => setDispCfg((p) => ({ ...p, [f]: v }));

  // Step 4 helpers
  const addCalcRow    = () => setNewCalcRows((p) => [...p, { _key: Date.now(), name: '', formula_type: 'channel_minus_channel', channel_a_id: null, channel_b_id: null, constant: null, custom_formula: '', channel_type: 'other', overwrite: false, position: p.length + calcChannels.length }]);
  const updateCalcRow = (key, f, v) => setNewCalcRows((p) => p.map((r) => r._key === key ? { ...r, [f]: v } : r));
  const removeCalcRow = (key) => setNewCalcRows((p) => p.filter((r) => r._key !== key));
  const queueDeleteCalc = (pk) => setDeleteCalcQueue((p) => new Set([...p, pk]));
  const startEditCalc = (cc) => { setEditCalcId(cc.id); setEditCalcData({ ...cc }); };
  const cancelEditCalc = () => { setEditCalcId(null); setEditCalcData({}); };
  const saveEditCalc = async (pk) => {
    setSaving(true);
    try {
      const updated = await updateCalculatedChannel(savedMachineId, pk, { name: editCalcData.name, formula_type: editCalcData.formula_type, channel_a_id: editCalcData.channel_a_id || null, channel_b_id: editCalcData.channel_b_id || null, constant: editCalcData.constant != null ? editCalcData.constant : null, custom_formula: editCalcData.custom_formula || '', channel_type: editCalcData.channel_type, overwrite: editCalcData.overwrite });
      setCalcChannels((p) => p.map((c) => c.id === pk ? updated : c));
      setEditCalcId(null);
    } catch (e) { setError(e?.response?.data?.error || 'Update failed'); }
    finally { setSaving(false); }
  };

  const pageTitle = isEdit
    ? `Edit Machine — ${machine.machine_id}`
    : 'Commission New Machine';

  return (
    <PageShell backPath={backPath} title={pageTitle} crumb={isEdit ? machine.machine_id : 'New Machine'}
      footer={
        <Group position="apart" style={{ width: '100%' }}>
          <Button variant="subtle" color="gray" onClick={() => onDone(false)}>Cancel</Button>
          <Group spacing="sm">
            {active > 0 && <Button variant="default" onClick={prevStep} disabled={saving}>← Back</Button>}
            <Button onClick={nextStep} loading={saving}
              styles={{ root: { backgroundColor: ATOMS_BLUE, '&:hover': { backgroundColor: 'var(--accent-hover)' } } }}>
              {active === 4 ? 'Save & Close' : 'Next →'}
            </Button>
          </Group>
        </Group>
      }
      stepper={
        <Stepper active={active} orientation="vertical" size="sm"
          styles={{
            step:            { padding: '8px 0' },
            stepLabel:       { fontWeight: 600, fontSize: 13 },
            stepDescription: { fontSize: 11 },
          }}>
          {STEPS.map((s) => <Stepper.Step key={s.label} label={s.label} description={s.description} />)}
        </Stepper>
      }
    >
      {error && <Alert color="red" mb="lg" radius="md">{error}</Alert>}

      {/* ═══ STEP 1 — IDENTITY ═══════════════════════════════════════════════ */}
      {active === 0 && (
        <Stack maw={520}>
          <TextInput label="Machine ID" placeholder="ATOMS-001" required disabled={isEdit}
            description="Must match the MQTT topic identifier. Cannot be changed after creation."
            {...form.getInputProps('machine_id')} />
          <TextInput label="Machine Name" placeholder="Site A — AWG 500" required
            {...form.getInputProps('machine_name')} />
          <Select label="Timezone" description="Used to convert MQTT timestamps to local time"
            data={TIMEZONE_OPTIONS} {...form.getInputProps('timezone_offset_minutes')} />
          {oems != null && (
            <Select label="Owned By" placeholder="Select OEM…"
              description="The OEM organisation that owns and manages this machine"
              data={oems} clearable {...form.getInputProps('owned_by_id')} />
          )}
        </Stack>
      )}

      {/* ═══ STEP 2 — CHANNELS ═══════════════════════════════════════════════ */}
      {active === 1 && (
        <Stack>
          <ChannelPayloadSummary configuredSummary={configuredSummary} observedSchema={observedSchema} />
          <Text size="sm" color="dimmed">
            The <strong>Internal Name</strong> is the stored channel key used across live status, reports, and historical data.
            Use the observed payload summary below to verify counts before naming channels.
            The <strong>Display Name</strong> can be updated any time.
            Check <strong>OEM Internal</strong> to hide from distributor white-labels.
          </Text>
          {channelsLoading ? <Loader /> : (
            <>
              {channels.filter((c) => !deleteQueue.has(c.id)).length > 0 && (
                <>
                  <Text size="xs" weight={600} color="dimmed" tt="uppercase">Existing Channels</Text>
                  <ScrollArea>
                    <Table striped highlightOnHover style={{ minWidth: 600 }}>
                      <thead><tr><th>Type</th><th>Internal Name</th><th>Display Name</th><th style={{ width: 100 }}>OEM Internal</th><th style={{ width: 48 }}></th></tr></thead>
                      <tbody>
                        {channels.filter((c) => !deleteQueue.has(c.id)).map((c) => (
                          <tr key={c.id}>
                            <td><Badge size="sm" color={typeColor[c.channel_type] || 'gray'} variant="light">{c.channel_type}</Badge></td>
                            <td><Tooltip label="Locked — cannot be changed"><Text size="sm" sx={{ fontFamily: 'monospace', cursor: 'help' }}>{c.internal_name}</Text></Tooltip></td>
                            <td><TextInput size="xs" value={c.display_name} onChange={(e) => updateExisting(c.id, 'display_name', e.currentTarget.value)} /></td>
                            <td><Checkbox size="xs" checked={c.is_oem_internal} onChange={(e) => updateExisting(c.id, 'is_oem_internal', e.currentTarget.checked)} /></td>
                            <td><ActionIcon color="red" size="sm" variant="subtle" onClick={() => queueDelete(c.id)}><MdDelete /></ActionIcon></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </ScrollArea>
                  <Divider />
                </>
              )}
              {newChannels.length > 0 && (
                <>
                  <Text size="xs" weight={600} color="dimmed" tt="uppercase">New Channels</Text>
                  <ScrollArea>
                    <Table style={{ minWidth: 600 }}>
                      <thead><tr><th>Type</th><th>Internal Name</th><th>Display Name</th><th style={{ width: 100 }}>OEM Internal</th><th style={{ width: 48 }}></th></tr></thead>
                      <tbody>
                        {newChannels.map((nc) => (
                          <tr key={nc._key}>
                            <td><Select size="xs" data={CHANNEL_TYPES} value={nc.channel_type} onChange={(v) => updateNewRow(nc._key, 'channel_type', v)} /></td>
                            <td><TextInput size="xs" placeholder="eg. lp_switch" value={nc.internal_name} onChange={(e) => updateNewRow(nc._key, 'internal_name', e.currentTarget.value.replace(/\s/g, '_').toLowerCase())} /></td>
                            <td><TextInput size="xs" placeholder="eg. LP Switch" value={nc.display_name} onChange={(e) => updateNewRow(nc._key, 'display_name', e.currentTarget.value)} /></td>
                            <td><Checkbox size="xs" checked={nc.is_oem_internal} onChange={(e) => updateNewRow(nc._key, 'is_oem_internal', e.currentTarget.checked)} /></td>
                            <td><ActionIcon color="red" size="sm" variant="subtle" onClick={() => removeNewRow(nc._key)}><MdDelete /></ActionIcon></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </ScrollArea>
                </>
              )}
              <div><Button variant="light" leftIcon={<MdAdd />} size="sm" onClick={addNewRow}>Add Channel</Button></div>
            </>
          )}
        </Stack>
      )}

      {/* ═══ STEP 3 — DISPLAY CONFIG ═════════════════════════════════════════ */}
      {active === 2 && (
        <Stack spacing="md">
          <ChannelBoard
            available={allChannelNames}
            statusChannels={dispCfg.status_channels}
            reportChannels={dispCfg.report_channels}
            dashboardChannels={dispCfg.dashboard_channels}
            kpiChannels={dispCfg.kpi_channels}
            reportLabels={dispCfg.report_channel_labels}
            onChange={(field, value) => setDisp(field, value)}
          />
        </Stack>
      )}

      {/* ═══ STEP 4 — CALCULATED CHANNELS ════════════════════════════════════ */}
      {active === 3 && (
        <Stack maw={720}>
          <Text size="sm" color="dimmed">
            Calculated channels are derived from raw values using a formula — computed on every MQTT message.
            Use <strong>Overwrite</strong> to replace an inaccurate machine-sent value.
          </Text>
          {calcLoading ? <Loader /> : (
            <Stack spacing="md">
              {calcChannels.filter((cc) => !deleteCalcQueue.has(cc.id)).map((cc) => {
                const isEditing = editCalcId === cc.id;
                const row = isEditing ? editCalcData : cc;
                return (
                  <Stack key={cc.id} spacing={6} p="md" sx={(t) => ({ border: `1px solid ${isEditing ? t.colors.blue[4] : t.colors.gray[3]}`, borderRadius: t.radius.sm, background: 'white' })}>
                    <Group position="apart">
                      {isEditing
                        ? <TextInput size="xs" placeholder="Channel name" value={row.name} onChange={(e) => setEditCalcData((p) => ({ ...p, name: e.currentTarget.value }))} sx={{ flex: 1 }} />
                        : <Group spacing={8}><Badge size="sm" color={typeColor[cc.channel_type] || 'gray'} variant="light">{cc.channel_type}</Badge><Text size="sm" weight={600}>{cc.name}</Text>{cc.overwrite && <Badge size="xs" color="orange">overwrite</Badge>}</Group>
                      }
                      <Group spacing={4}>
                        {isEditing
                          ? <><ActionIcon size="sm" color="blue" variant="subtle" loading={saving} onClick={() => saveEditCalc(cc.id)}><MdSave /></ActionIcon><ActionIcon size="sm" variant="subtle" onClick={cancelEditCalc}><MdClose /></ActionIcon></>
                          : <><ActionIcon size="sm" variant="subtle" onClick={() => startEditCalc(cc)}><MdEdit /></ActionIcon><ActionIcon size="sm" color="red" variant="subtle" onClick={() => queueDeleteCalc(cc.id)}><MdDelete /></ActionIcon></>
                        }
                      </Group>
                    </Group>
                    {isEditing
                      ? <CalcRowFields row={editCalcData} onChange={(f, v) => setEditCalcData((p) => ({ ...p, [f]: v }))} channelOptions={channelOptions} />
                      : <Text size="xs" color="dimmed">{cc.formula_type.replace(/_/g, ' ')}{cc.channel_a_name ? ` | A: ${cc.channel_a_name}` : ''}{cc.channel_b_name ? ` | B: ${cc.channel_b_name}` : ''}{cc.constant != null ? ` | ×${cc.constant}` : ''}{cc.custom_formula ? ` | ${cc.custom_formula}` : ''}</Text>
                    }
                  </Stack>
                );
              })}
              {newCalcRows.map((nc) => (
                <Stack key={nc._key} spacing={6} p="md" sx={(t) => ({ border: `1px dashed ${t.colors.green[5]}`, borderRadius: t.radius.sm, background: 'white' })}>
                  <Group position="apart">
                    <Badge color="green" variant="light">NEW</Badge>
                    <ActionIcon size="sm" color="red" variant="subtle" onClick={() => removeCalcRow(nc._key)}><MdDelete /></ActionIcon>
                  </Group>
                  <TextInput size="sm" label="Output Channel Name" placeholder="e.g. Idle Time" value={nc.name} onChange={(e) => updateCalcRow(nc._key, 'name', e.currentTarget.value)} />
                  <CalcRowFields row={nc} onChange={(f, v) => updateCalcRow(nc._key, f, v)} channelOptions={channelOptions} />
                </Stack>
              ))}
              <div><Button variant="light" leftIcon={<MdAdd />} size="sm" onClick={addCalcRow}>Add Calculated Channel</Button></div>
            </Stack>
          )}
        </Stack>
      )}

      {/* ═══ STEP 5 — ALARM RULES ════════════════════════════════════════════ */}
      {active === 4 && (
        <Stack maw={780}>
          <Text size="sm" color="dimmed">
            Configure which channel values trigger push notifications and alarm logs.
            Rules are evaluated on every MQTT message. Each rule fires at most once per cooldown period.
          </Text>

          {alarmLoading ? <Loader /> : (
            <Stack spacing="md">

              {/* Existing rules */}
              {alarmRules.filter((r) => !deleteAlarmQueue.has(r.id)).length > 0 && (
                <>
                  <Text size="xs" weight={600} color="dimmed" tt="uppercase">Existing Alarm Rules</Text>
                  {alarmRules.filter((r) => !deleteAlarmQueue.has(r.id)).map((rule) => (
                    <div key={rule.id} style={{
                      border: '1px solid #dee2e6', borderRadius: 8, padding: '12px 16px',
                      background: rule.is_active ? 'white' : '#f8f9fa',
                      borderLeft: `4px solid ${rule.severity === 'critical' ? '#C42B1C' : rule.severity === 'warning' ? '#B45309' : '#0897BE'}`,
                      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <Badge size="sm" color={severityColor[rule.severity] || 'gray'} variant="light">
                            {rule.severity}
                          </Badge>
                          <Text size="sm" weight={600}>{rule.title}</Text>
                          {!rule.is_active && <Badge size="xs" color="gray" variant="outline">Disabled</Badge>}
                        </div>
                        <Text size="xs" color="dimmed" sx={{ fontFamily: 'monospace' }}>
                          {rule.channel_internal_name} {rule.condition} {rule.threshold != null ? rule.threshold : ''} · cooldown {rule.cooldown_minutes}m
                        </Text>
                        <Text size="xs" color="dimmed" mt={2}>{rule.message_template}</Text>
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <ActionIcon
                          size="sm" variant="subtle"
                          color={rule.is_active ? 'gray' : 'green'}
                          title={rule.is_active ? 'Disable rule' : 'Enable rule'}
                          onClick={async () => {
                            try {
                              const updated = await updateAlarmRule(savedMachineId, rule.id, { is_active: !rule.is_active });
                              setAlarmRules((p) => p.map((r) => r.id === rule.id ? updated : r));
                            } catch {}
                          }}
                        >
                          {rule.is_active ? '⏸' : '▶'}
                        </ActionIcon>
                        <ActionIcon size="sm" color="red" variant="subtle"
                          onClick={() => setDeleteAlarmQueue((p) => new Set([...p, rule.id]))}>
                          <MdDelete />
                        </ActionIcon>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* New rule rows */}
              {newAlarmRows.map((row) => (
                <div key={row._key} style={{ border: '1px dashed #40c057', borderRadius: 8, padding: '14px 16px', background: 'white' }}>
                  <Group position="apart" mb="sm">
                    <Badge color="green" variant="light">NEW RULE</Badge>
                    <ActionIcon size="sm" color="red" variant="subtle"
                      onClick={() => setNewAlarmRows((p) => p.filter((r) => r._key !== row._key))}>
                      <MdDelete />
                    </ActionIcon>
                  </Group>
                  <Stack spacing={8}>
                    <Group grow>
                      <Select
                        size="sm" label="Channel" searchable required
                        placeholder="Select or type channel name…"
                        data={allChannelNames.map((n) => ({ value: n, label: n }))}
                        value={row.channel_internal_name || null}
                        onChange={(v) => setNewAlarmRows((p) => p.map((r) => r._key === row._key ? { ...r, channel_internal_name: v || '' } : r))}
                        creatable
                        getCreateLabel={(q) => `+ Use "${q}"`}
                        onCreate={(q) => {
                          setNewAlarmRows((p) => p.map((r) => r._key === row._key ? { ...r, channel_internal_name: q } : r));
                          return q;
                        }}
                      />
                      <Select
                        size="sm" label="Condition" required
                        data={CONDITION_OPTIONS}
                        value={row.condition}
                        onChange={(v) => setNewAlarmRows((p) => p.map((r) => r._key === row._key ? { ...r, condition: v } : r))}
                      />
                      {!DIGITAL_CONDITIONS.has(row.condition) && (
                        <NumberInput
                          size="sm" label="Threshold" required precision={4}
                          value={row.threshold !== '' ? row.threshold : ''}
                          onChange={(v) => setNewAlarmRows((p) => p.map((r) => r._key === row._key ? { ...r, threshold: v } : r))}
                        />
                      )}
                    </Group>
                    <Group grow>
                      <TextInput
                        size="sm" label="Alarm Title" required placeholder="e.g. High Temperature"
                        value={row.title}
                        onChange={(e) => setNewAlarmRows((p) => p.map((r) => r._key === row._key ? { ...r, title: e.currentTarget.value } : r))}
                      />
                      <Select
                        size="sm" label="Severity"
                        data={SEVERITY_OPTIONS}
                        value={row.severity}
                        onChange={(v) => setNewAlarmRows((p) => p.map((r) => r._key === row._key ? { ...r, severity: v } : r))}
                      />
                      <NumberInput
                        size="sm" label="Cooldown (min)" min={1} max={1440}
                        value={row.cooldown_minutes}
                        onChange={(v) => setNewAlarmRows((p) => p.map((r) => r._key === row._key ? { ...r, cooldown_minutes: v } : r))}
                      />
                    </Group>
                    <TextInput
                      size="sm" label="Message Template"
                      description="Use {machine}, {channel}, {value} as placeholders"
                      value={row.message_template}
                      onChange={(e) => setNewAlarmRows((p) => p.map((r) => r._key === row._key ? { ...r, message_template: e.currentTarget.value } : r))}
                    />
                  </Stack>
                </div>
              ))}

              <div>
                <Button variant="light" leftIcon={<MdAdd />} size="sm"
                  onClick={() => setNewAlarmRows((p) => [...p, BLANK_ALARM()])}>
                  Add Alarm Rule
                </Button>
              </div>

              {alarmRules.length === 0 && newAlarmRows.length === 0 && (
                <div style={{ padding: '32px 24px', textAlign: 'center', border: '1px dashed #dee2e6', borderRadius: 8 }}>
                  <Text color="dimmed" size="sm">No alarm rules configured yet.</Text>
                  <Text color="dimmed" size="xs" mt={4}>
                    Add rules to get push notifications when channel values exceed thresholds.
                  </Text>
                </div>
              )}
            </Stack>
          )}
        </Stack>
      )}
    </PageShell>
  );
};

// ── CalcRowFields ─────────────────────────────────────────────────────────────

const ChannelPayloadSummary = ({ configuredSummary = {}, observedSchema = null }) => {
  const observedGroups = observedSchema?.groups || {};

  return (
    <Stack spacing="sm">
      <Alert color="blue" variant="light" radius="md">
        <Text size="sm" weight={600}>Payload shape cross-check</Text>
        <Text size="xs" color="dimmed" mt={4}>
          Compare what is configured for this machine against the most recently observed MQTT payload schema.
        </Text>
        <Text size="xs" color="dimmed" mt={4}>
          {observedSchema?.available
            ? `Observed source: ${observedSchema.source === 'last_machine_data' ? 'LastMachineData' : 'MachineDataLog'}${observedSchema.timestamp ? ` | ${observedSchema.timestamp}` : ''}`
            : 'No stored payload found yet for this machine. Counts below will update after the first MQTT record is saved.'}
        </Text>
      </Alert>

      <Table withBorder withColumnBorders striped style={{ minWidth: 720, background: 'white' }}>
        <thead>
          <tr>
            <th>Group</th>
            <th>Configured</th>
            <th>Observed</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {CHANNEL_GROUP_META.map(({ key, label }) => {
            const configured = configuredSummary[key] || { count: 0, names: [] };
            const observed = observedGroups[key] || { count: 0, names: [] };
            const countsMatch = observedSchema?.available ? configured.count === observed.count : null;

            return (
              <tr key={key}>
                <td>
                  <Text size="sm" weight={600}>{label}</Text>
                </td>
                <td>
                  <Stack spacing={4}>
                    <Badge variant="light" color="gray">{configured.count} configured</Badge>
                    <Text size="xs" color="dimmed">
                      {(configured.names || []).filter(Boolean).join(', ') || 'No configured names'}
                    </Text>
                  </Stack>
                </td>
                <td>
                  <Stack spacing={4}>
                    <Badge variant="light" color={observedSchema?.available ? 'blue' : 'gray'}>
                      {observed.count} observed
                    </Badge>
                    <Text size="xs" color="dimmed">
                      {(observed.names || []).filter(Boolean).join(', ') || (observedSchema?.available ? 'Names not available in stored payload' : 'No observed payload yet')}
                    </Text>
                  </Stack>
                </td>
                <td>
                  {countsMatch == null ? (
                    <Badge variant="outline" color="gray">Waiting for payload</Badge>
                  ) : countsMatch ? (
                    <Badge variant="light" color="green">Count match</Badge>
                  ) : (
                    <Badge variant="light" color="red">Mismatch</Badge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Stack>
  );
};

const CalcRowFields = ({ row, onChange, channelOptions }) => {
  const ft = row.formula_type || '';
  return (
    <Stack spacing={8}>
      <Select size="sm" label="Formula Type" data={FORMULA_TYPES} value={ft} onChange={(v) => onChange('formula_type', v)} />
      {needsChannelA.has(ft) && (
        <Select size="sm" label={ft === 'elapsed_minus_channel' ? 'Channel to subtract from elapsed time' : 'Channel A'}
          placeholder="Select channel…" data={channelOptions}
          value={row.channel_a_id ? String(row.channel_a_id) : null}
          onChange={(v) => onChange('channel_a_id', v ? parseInt(v) : null)} clearable />
      )}
      {needsChannelB.has(ft) && (
        <Select size="sm" label="Channel B" placeholder="Select channel…" data={channelOptions}
          value={row.channel_b_id ? String(row.channel_b_id) : null}
          onChange={(v) => onChange('channel_b_id', v ? parseInt(v) : null)} clearable />
      )}
      {needsConstant.has(ft) && (
        <NumberInput size="sm" label="Constant" placeholder="e.g. 3600"
          value={row.constant ?? ''} onChange={(v) => onChange('constant', v !== '' ? v : null)} precision={4} />
      )}
      {needsCustom.has(ft) && (
        <Textarea size="sm" label="Custom Formula" minRows={2}
          placeholder="e.g. hms_to_sec(Run_Time_HH_MM_SS_) / 3600"
          description="Use internal_name with special chars replaced by _. Helpers: hms_to_sec(s), sec_to_hms(n), elapsed_sec()"
          value={row.custom_formula || ''} onChange={(e) => onChange('custom_formula', e.currentTarget.value)} />
      )}
      <Group spacing="xl" align="flex-start">
        <Select size="sm" label="Output Channel Type"
          data={CHANNEL_TYPES} value={row.channel_type || 'other'}
          onChange={(v) => onChange('channel_type', v)} sx={{ flex: 1 }} />
        <Switch mt={24} size="sm" label="Overwrite machine value"
          description="Replace inaccurate machine-sent channel with this result"
          checked={row.overwrite || false}
          onChange={(e) => onChange('overwrite', e.currentTarget.checked)} />
      </Group>
    </Stack>
  );
};

// ── PageShell — layout wrapper ────────────────────────────────────────────────

const PageShell = ({ backPath, title, crumb, stepper, footer, children }) => {
  const navigate = useNavigate();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f4f6f8' }}>

      {/* Header */}
      <div style={{ background: ATOMS_BLUE, color: 'white', height: 56, display: 'flex', alignItems: 'center', padding: '0 24px', flexShrink: 0, gap: 16 }}>
        <ActionIcon variant="transparent" onClick={() => navigate(backPath)} title="Back"
          sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', background: 'rgba(255,255,255,0.1)' } }}>
          <MdArrowBack size={20} />
        </ActionIcon>
        <Image src={AtomsLogo} width={28} height={28} fit="contain" style={{ flexShrink: 0 }} />
        <Breadcrumbs separator="›"
          styles={{ separator: { color: 'rgba(255,255,255,0.4)', margin: '0 6px' } }}>
          <Anchor size="sm" onClick={() => navigate(backPath)}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' }, cursor: 'pointer' }}>
            Admin Panel
          </Anchor>
          <Text size="sm" sx={{ color: 'white', fontWeight: 600 }}>{crumb}</Text>
        </Breadcrumbs>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Sidebar */}
        {stepper && (
          <div style={{ width: 220, background: 'white', borderRight: '1px solid #dee2e6', padding: '32px 20px', flexShrink: 0, overflowY: 'auto' }}>
            {stepper}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '40px 48px' }}>
          {children}
        </div>
      </div>

      {/* Footer */}
      {footer && (
        <div style={{ background: 'white', borderTop: '1px solid #dee2e6', padding: '0 48px', height: 64, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {footer}
        </div>
      )}
    </div>
  );
};

export default MachineEditPage;



